/**
 * v3.25 任务中心（第 5 个 Tab「任务」）。
 *
 * 四块：① 今日挖矿摘要；② 每日任务（签到上链，复用 MePage 同款逻辑）；
 * ③ 新手任务（NEWCOMER_TASKS 7 项，完成态实时检测，点击跳转/执行）；
 * ④ 行为挖矿任务卡（引导 → 广场/发现做行为，DPB 由链上行为挖矿发放，不另发）。
 * 进度仅做引导与记录，不发额外链上奖励（与 tasks.ts 设计一致）。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useIdentityStore } from '@/store/identity';
import { useChatStore } from '@/store/chat';
import { useGroupStore } from '@/store/group';
import { useUiStore } from '@/store/ui';
import { useSocialStore } from '@/store/social';
import { container } from '@/services/container';
import { MeshLinkError } from '@/lib/result';
import { NEWCOMER_TASKS, computeTaskDone } from '@/utils/tasks';
import { ROUTE_PATH } from '@/router';

/** 行为挖矿任务卡（文案与链上全行为挖矿配置对齐；奖励由链上行为挖矿发放）。 */
const MINING_TASKS: Array<{ icon: string; title: string; desc: string; to: string }> = [
  { icon: '✍️', title: '发文 / 发图', desc: '发帖上链，进入推荐流并获得行为奖励', to: '/feed' },
  { icon: '🎬', title: '发视频', desc: '发布视频动态，视频播放行为上链挖矿', to: '/feed' },
  { icon: '🎤', title: '发语音 / 直播', desc: '语音动态与直播观看时长计入行为挖矿', to: '/feed' },
  { icon: '👍', title: '点赞 / 转发 / 评论', desc: '互动上链，赞与转发均有 DPB 奖励', to: '/feed' },
  { icon: '👀', title: '浏览消费', desc: '浏览帖子与视频，浏览行为即挖矿', to: '/feed' },
  { icon: '📸', title: '朋友圈互动', desc: '好友动态点赞评论，互动上链', to: '/circle' },
];

/** 邀请里程碑（进度读本机绑定态；阶梯奖励随邀请码体系）。 */
const INVITE_MILESTONES: Array<{ n: number; reward: string }> = [
  { n: 1, reward: '双方推荐奖励（邀请码绑定即发）' },
  { n: 3, reward: '邀请 3 人 · 额外行为挖矿加成（规划中）' },
  { n: 10, reward: '邀请 10 人 · 创作者徽章（规划中）' },
];

export function TasksPage(): JSX.Element {
  const navigate = useNavigate();
  const status = useIdentityStore((s) => s.status);
  const contacts = useChatStore((s) => s.contacts);
  const groups = useGroupStore((s) => s.groups);
  const following = useSocialStore((s) => s.following);

  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinState, setCheckinState] = useState<{ today: boolean; streak: number }>(() => {
    try {
      const raw = window.localStorage.getItem('mesh_checkin');
      const parsed = raw ? (JSON.parse(raw) as { date: string; streak: number }) : null;
      const today = new Date().toISOString().slice(0, 10);
      if (!parsed) return { today: false, streak: 0 };
      if (parsed.date === today) return { today: true, streak: parsed.streak };
      const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
      const streak = parsed.date === yesterday ? Math.min(7, parsed.streak + 1) : 1;
      return { today: false, streak };
    } catch {
      return { today: false, streak: 0 };
    }
  });

  /** 每日签到：checkin 上链 → 签到挖矿（首日 0.0005 / 连续 ≥2 天 0.001 DPB）。 */
  const doCheckin = async (): Promise<void> => {
    if (checkingIn || checkinState.today) return;
    const addr = container.identity.address;
    if (!addr) {
      useUiStore.getState().pushError('E_LOCKED');
      return;
    }
    setCheckingIn(true);
    try {
      await container.chain.checkin({
        from: addr as `0x${string}`,
        ts: Date.now(),
        signature: container.identity.signObject({ checkin: addr, ts: Date.now() }),
      });
      const streak = checkinState.streak === 0 ? 1 : Math.min(7, checkinState.streak + 1);
      try {
        window.localStorage.setItem(
          'mesh_checkin',
          JSON.stringify({ date: new Date().toISOString().slice(0, 10), streak }),
        );
      } catch {
        /* 存储满忽略 */
      }
      setCheckinState({ today: true, streak });
      useUiStore.getState().pushToast('success', `签到成功（连续 ${streak} 天，奖励已上链）`);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setCheckingIn(false);
    }
  };

  /** 新手任务完成态（动态检测）。 */
  const taskCtx = {
    unlocked: status === 'unlocked',
    checkinToday: checkinState.today,
    contactCount: contacts.length,
    groupCount: groups.length,
  };
  const taskDone = (id: (typeof NEWCOMER_TASKS)[number]['id']): boolean => computeTaskDone(id, taskCtx);
  const doneCount = NEWCOMER_TASKS.filter((t) => taskDone(t.id)).length;

  /** 点击未完成任务：执行对应动作 / 跳转到对应入口。 */
  const runTask = (id: (typeof NEWCOMER_TASKS)[number]['id']): void => {
    if (taskDone(id)) return;
    switch (id) {
      case 'unlock':
        navigate(ROUTE_PATH.unlock);
        break;
      case 'referral':
        navigate(ROUTE_PATH.me);
        break;
      case 'checkin':
        void doCheckin();
        break;
      case 'post':
        navigate(ROUTE_PATH.feed);
        break;
      case 'contact':
        navigate(ROUTE_PATH.discover);
        break;
      case 'official':
        navigate(ROUTE_PATH.discover);
        break;
      case 'group':
        navigate(ROUTE_PATH.chat);
        break;
      default:
        break;
    }
  };

  const invited = following.length;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
      {/* 今日挖矿摘要 */}
      <div className="rounded-card bg-panel-2 p-4">
        <p className="text-[11px] font-medium text-dim">今日任务 · 行为即挖矿</p>
        <div className="mt-2 flex items-end justify-between">
          <div>
            <p className="text-lg font-semibold text-text">
              新手任务 {doneCount}/{NEWCOMER_TASKS.length}
            </p>
            <p className="text-[11px] text-dim">
              关注 {following.length} · 好友 {contacts.length} · 群 {groups.length}
            </p>
          </div>
          <Badge tone="gold">⛏️ 行为挖矿已开启</Badge>
        </div>
      </div>

      {/* 每日任务：签到 */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-text">📅 每日任务</h2>
        <div className="flex items-center justify-between rounded-card bg-panel-2 p-4">
          <div>
            <p className="text-sm text-text">每日签到</p>
            <p className="text-[11px] text-dim">
              {checkinState.today
                ? `今日已签 · 连续 ${checkinState.streak} 天（奖励已上链）`
                : checkinState.streak > 0
                  ? `连续 ${checkinState.streak} 天 · 今日签到得 0.001 DPB`
                  : '首次签到得 0.0005 DPB'}
            </p>
          </div>
          <Button size="sm" loading={checkingIn} disabled={checkinState.today} onClick={() => void doCheckin()}>
            {checkinState.today ? '已签到 ✓' : '签到'}
          </Button>
        </div>
      </section>

      {/* 新手任务 */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-text">🎯 新手任务</h2>
        <div className="space-y-2">
          {NEWCOMER_TASKS.map((t) => {
            const done = taskDone(t.id);
            return (
              <button
                key={t.id}
                type="button"
                disabled={done}
                onClick={() => runTask(t.id)}
                className={`flex w-full items-center gap-3 rounded-card px-3 py-2.5 text-left transition-colors ${
                  done ? 'bg-panel-2/60 opacity-70' : 'bg-panel-2 hover:bg-panel'
                }`}
              >
                <span className="text-xl" aria-hidden>
                  {t.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-text">{t.title}</span>
                  <span className="block truncate text-[11px] text-dim">{t.desc}</span>
                </span>
                {done ? (
                  <Badge tone="ok">已完成</Badge>
                ) : (
                  <span className="text-xs text-accent">去完成 →</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* 行为挖矿任务卡 */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-text">⛏️ 行为挖矿</h2>
        <div className="grid grid-cols-2 gap-2">
          {MINING_TASKS.map((m) => (
            <button
              key={m.title}
              type="button"
              onClick={() => navigate(m.to)}
              className="rounded-card bg-panel-2 px-3 py-2.5 text-left transition-colors hover:bg-panel"
            >
              <p className="text-sm text-text">
                <span className="mr-1" aria-hidden>
                  {m.icon}
                </span>
                {m.title}
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-dim">{m.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* 邀请里程碑 */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-text">🤝 邀请里程碑</h2>
        <div className="space-y-2">
          {INVITE_MILESTONES.map((m) => {
            const reached = invited >= m.n;
            return (
              <div key={m.n} className="flex items-center justify-between rounded-card bg-panel-2 px-3 py-2.5">
                <div>
                  <p className="text-sm text-text">
                    邀请 {m.n} 人{reached ? ' · 已达成 ✓' : ''}
                  </p>
                  <p className="text-[11px] text-dim">{m.reward}</p>
                </div>
                <Badge tone={reached ? 'ok' : 'neutral'}>{reached ? '达成' : `${invited}/${m.n}`}</Badge>
              </div>
            );
          })}
          <Button block size="sm" onClick={() => navigate(ROUTE_PATH.me)}>
            生成邀请链接 / 二维码
          </Button>
        </div>
      </section>
    </div>
  );
}

export default TasksPage;
